import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useChangeLimitStore } from '@lib/store';
import { useAccountContext } from '@shared/hooks';
import { FormatUtils, PaletteUtils } from '@shared/utils';
import { MobileHeader, Success } from '@widgets';
import { Button, Spinner, TextInput } from '@shared/widgets';

interface ICardLimitForm {
  creditLimit?: number;
  password: string;
}
const DEFAULT_VALUES: ICardLimitForm = { creditLimit: undefined, password: '' };

export const CardLimit = () => {
  const changeLimitStore = useChangeLimitStore();

  const { account } = useAccountContext();
  const cardLimitForm = useForm({ defaultValues: DEFAULT_VALUES });
  const { t } = useTranslation('card');

  useEffect(() => {
    return () => changeLimitStore.clear();
  }, []);

  const onSubmit = (data: ICardLimitForm) => {
    if (!account) return;
    changeLimitStore.change({ accountId: account.id, creditLimit: data.creditLimit!, password: data.password });
  };

  if (!account?.id) return null;
  return (
    <>
      <MobileHeader />
      <form
        className='p-10 text-base font-medium relative min-h-[400px] relative'
        noValidate
        autoComplete='off'
        onSubmit={cardLimitForm.handleSubmit(onSubmit)}
      >
        {changeLimitStore.loading && <Spinner position dark />}
        {changeLimitStore.success && <Success text={t('manageCardLimit.success')} />}
        <p className='text-xl my-4'>{t('manageCardLimit.title')}</p>
        <ul className='mt-[50px] mx-0 mb-0 flex-nowrap flex max-[840]:flex-col'>
          <li className='mr-5 flex-[1_1_380px] max-[840px]:flex-none min-h-[220px] mb-5 pr-5 pl-[50px] flex flex-col justify-center border border-[#c9c9c9] max-[840px]:mr-0'>
            <p>{t('manageCardLimit.description')}</p>
            <p
              className={clsx('text-[24px] font-[600] py-6 -tracking-[0.2px] ', PaletteUtils.isBordierPalette ? 'text-[#dc291c]' : 'text-[#a47432]')}
            >
              {FormatUtils.formatAmount(account.limit, account.currency)}
            </p>
          </li>
          <li className='flex-[1_1_380px] max-[840px]:flex-none min-h-[220px] mb-5 pr-5 pl-[50px] flex flex-col justify-center border border-[#c9c9c9]'>
            <Controller
              name='creditLimit'
              control={cardLimitForm.control}
              rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('manageCardLimit.placeholders.newLimitRequested') }) }}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  className='max-w-[290px]'
                  formStyle
                  caption={t('manageCardLimit.placeholders.newLimitRequested')}
                  name='creditLimit'
                  type='text'
                  required
                  error={error?.message}
                />
              )}
            />
            <Controller
              name='password'
              control={cardLimitForm.control}
              rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.password', { ns: 'common' }) }) }}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  className='max-w-[290px]'
                  formStyle
                  caption={t('placeholders.password', { ns: 'common' })}
                  type='password'
                  name='password'
                  required
                  error={error?.message || (changeLimitStore.error === 'Wrong password' ? t('errors.form.password.wrong') : undefined)}
                />
              )}
            />
          </li>
        </ul>
        <Button className='px-5 -tracking-[0.2px] ml-auto' view='secondary' caption={t('buttons.continue', { ns: 'common' })} type='submit' />
      </form>
    </>
  );
};
